import { Button } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineEye,
} from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllEventsShop } from "../../redux/actions/event";
import { server, backend_url } from "../../server";
import { IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import { EVENT_BANNER_SIZE_LABEL } from "../../constants/eventBanner";
import EventBannerCropModal from "./EventBannerCropModal";
import { useEventBannerUpload } from "../../hooks/useEventBannerUpload";
import Loader from "../Layout/Loader";
import DashboardTableScroll from "../shared/DashboardTableScroll";

const initialFormState = {
  name: "",
  bannerLink: "",
  description: "",
};

const AllEvents = ({
  entityLabel = "Event",
  entityLabelPlural = "Events",
  apiBasePath = `${server}/event`,
  apiNamespace = "event",
}) => {
  const isService = String(entityLabel || "").toLowerCase() === "service";
  const dispatch = useDispatch();
  const { events, isLoading } = useSelector((state) => state.events);
  const { user } = useSelector((state) => state.user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formState, setFormState] = useState(initialFormState);
  const [bannerFile, setBannerFile] = useState(null);
  const [amendmentBannerFile, setAmendmentBannerFile] = useState(null);
  const {
    cropSession: editCropSession,
    handleFileSelect: handleEditBannerFileSelect,
    handleCropConfirm: handleEditCropConfirm,
    handleCropCancel: handleEditCropCancel,
  } = useEventBannerUpload(setBannerFile);
  const {
    cropSession: amendmentCropSession,
    handleFileSelect: handleAmendmentBannerFileSelect,
    handleCropConfirm: handleAmendmentCropConfirm,
    handleCropCancel: handleAmendmentCropCancel,
  } = useEventBannerUpload(setAmendmentBannerFile);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [timeFilter, setTimeFilter] = useState("all"); // "all", "days", "week", "month"
  const [amendmentModalOpen, setAmendmentModalOpen] = useState(false);
  const [amendmentEvent, setAmendmentEvent] = useState(null);
  const [amendmentMessage, setAmendmentMessage] = useState("");
  const [amendmentName, setAmendmentName] = useState("");
  const [amendmentDescription, setAmendmentDescription] = useState("");
  const [amendmentBannerLink, setAmendmentBannerLink] = useState("");
  const [amendmentSending, setAmendmentSending] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: "",
    message: "",
    confirmLabel: "Confirm",
  });
  const confirmModalResolveRef = useRef(null);

  // Use ref to track previous user ID and prevent duplicate fetches
  const prevUserIdRef = useRef(null);
  const hasFetchedRef = useRef(false);
  
  useEffect(() => {
    const currentUserId = user?._id;
    const isSeller = user?.isSeller;
    
    // Reset fetch flag if user ID changed to allow refetch for new user
    if (currentUserId && currentUserId !== prevUserIdRef.current) {
      prevUserIdRef.current = currentUserId;
      hasFetchedRef.current = false; // Reset to allow fetch for new user
    }
    
    // Fetch if user is a seller and we haven't fetched yet for this user
    // This handles both initial load and direct navigation
    const shouldFetch = currentUserId && isSeller && !hasFetchedRef.current;
    
    if (shouldFetch) {
      hasFetchedRef.current = true;
      dispatch(getAllEventsShop(currentUserId, apiNamespace));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.isSeller, dispatch, apiNamespace]); // Include dispatch for completeness

  // Memoize getStartDate function to prevent recreation on every render
  const getStartDate = useCallback((event) => {
    return event.approvedStart || event.preferredStart || null;
  }, []);

  // Filter and sort events based on time filter and start date - optimized to prevent blocking
  const filteredAndSortedEvents = useMemo(() => {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return [];
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Limit processing to prevent blocking with large datasets
    const maxEvents = 500;
    const eventsToProcess = events.slice(0, maxEvents);

    let filtered = [];

    // Filter by time period based on start date - optimized loop
    if (timeFilter !== "all") {
      for (let i = 0; i < eventsToProcess.length; i++) {
        const event = eventsToProcess[i];
        if (!event) continue;
        
        const startDate = event.approvedStart || event.preferredStart || null;
        if (!startDate) continue;

        try {
          const eventStart = new Date(startDate);
          if (isNaN(eventStart.getTime())) continue;
          
          eventStart.setHours(0, 0, 0, 0);
          const diffTime = eventStart - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          let shouldInclude = false;
          if (timeFilter === "days") {
            shouldInclude = diffDays >= 0 && diffDays <= 7;
          } else if (timeFilter === "week") {
            shouldInclude = diffDays >= 0 && diffDays <= 7;
          } else if (timeFilter === "month") {
            shouldInclude = diffDays >= 0 && diffDays <= 30;
          }
          
          if (shouldInclude) {
            filtered.push(event);
          }
        } catch (err) {
          continue; // Skip invalid dates
        }
      }
    } else {
      filtered = [...eventsToProcess];
    }

    // Sort by start date (earliest first), events without start date go to end - optimized
    filtered.sort((a, b) => {
      const dateA = a.approvedStart || a.preferredStart || null;
      const dateB = b.approvedStart || b.preferredStart || null;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // A goes to end
      if (!dateB) return -1; // B goes to end

      try {
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        if (isNaN(timeA) || isNaN(timeB)) return 0;
        return timeA - timeB;
      } catch {
        return 0;
      }
    });

    return filtered;
  }, [events, timeFilter]);

  // Memoize rows to prevent unnecessary recalculations on every render
  const rows = useMemo(() => {
    if (!filteredAndSortedEvents || !Array.isArray(filteredAndSortedEvents)) return [];
    return filteredAndSortedEvents.map((event) => {
      const startDate = getStartDate(event);
      return {
        id: event._id,
        name: event.name || "",
        bannerLink: event.bannerLink || "",
        bannerImage: event.bannerImage || "",
        status: event.status || "pending",
        durationWeeks: event.durationWeeks || 0,
        billingCycle: event.billingCycle || (Number(event.durationWeeks || 0) >= 4 ? "monthly" : "weekly"),
        nextBillingDate: event.nextBillingDate || null,
        lastBillingStatus: event.lastBillingStatus || "none",
        isSubscriptionActive: event.isSubscriptionActive !== false,
        autoRenew: event.autoRenew !== false,
        paymentPlan: event.paymentPlan || "",
        subscriptionCancelledAt: event.subscriptionCancelledAt || null,
        preferredStart: event.preferredStart,
        approvedStart: event.approvedStart,
        approvedEnd: event.approvedEnd,
        startDate: startDate, // Add computed start date for sorting
        totalAmount: event.totalAmount || 0,
        currency: event.currency || "GBP",
        paymentMethod: event.paymentMethod || "wallet",
        walletAmount: event.walletAmount || 0,
        paymentIntentId: event.paymentIntentId || "",
        original: event,
      };
    });
  }, [filteredAndSortedEvents, getStartDate]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const closeConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
    const resolve = confirmModalResolveRef.current;
    confirmModalResolveRef.current = null;
    if (resolve) resolve(false);
  }, []);

  const openConfirmModal = useCallback(({ title, message, confirmLabel = "Confirm" }) => {
    return new Promise((resolve) => {
      confirmModalResolveRef.current = resolve;
      setConfirmModalConfig({ title, message, confirmLabel });
      setConfirmModalOpen(true);
    });
  }, []);

  const handleConfirmModalConfirm = useCallback(() => {
    const resolve = confirmModalResolveRef.current;
    confirmModalResolveRef.current = null;
    setConfirmModalOpen(false);
    if (resolve) resolve(true);
  }, []);

  const getProjectedEndDate = (row) => {
    if (row?.approvedEnd) return row.approvedEnd;
    if (!row?.preferredStart || !row?.durationWeeks) return null;
    const start = new Date(row.preferredStart);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + Number(row.durationWeeks) * 7);
    return end;
  };

  const renderStatusBadge = (status) => {
    const normalized = (status || "").toLowerCase();
    const stylesMap = {
      pending: "bg-amber-100 text-amber-700",
      active: "bg-emerald-100 text-emerald-700",
      expired: "bg-slate-200 text-slate-600",
      rejected: "bg-rose-100 text-rose-700",
      payment_due: "bg-rose-100 text-rose-700",
    };
    const label = {
      pending: "Pending approval",
      active: "Active",
      expired: "Expired",
      rejected: "Rejected",
      payment_due: "Payment due — hidden",
    }[normalized] || status;
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${stylesMap[normalized] ||
          "bg-slate-100 text-slate-600"}`}
      >
        {label}
      </span>
    );
  };

  const isEventApproved = useCallback((status) => {
    const s = String(status || "").toLowerCase();
    return ["active", "approved", "running"].includes(s);
  }, []);

  const getRenewingSubscriptionHint = useCallback((evt) => {
    const pp = String(evt?.paymentPlan || "").toLowerCase();
    const duration = Number(evt?.durationWeeks || 0);
    const weeklyMulti = pp === "weekly" && duration > 1;
    return weeklyMulti || evt?.autoRenew === true;
  }, []);

  const isCancellationRecorded = useCallback((evt) => Boolean(evt?.subscriptionCancelledAt), []);

  const openEditModal = useCallback((event) => {
    if (!event) return;
    if (isEventApproved(event.status)) {
      setAmendmentEvent(event);
      setAmendmentMessage("");
      setAmendmentName(event.name || "");
      setAmendmentDescription(event.description || "");
      setAmendmentBannerLink(event.bannerLink || "");
      setAmendmentBannerFile(null);
      setAmendmentModalOpen(true);
      return;
    }
    setCurrentEvent(event);
    setFormState({
      name: event.name || "",
      bannerLink: event.bannerLink || "",
      description: event.description || "",
    });
    setBannerFile(null);
    setIsModalOpen(true);
  }, [isEventApproved]);

  const openAmendmentModal = useCallback((event) => {
    if (!event) return;
    setAmendmentEvent(event);
    setAmendmentMessage("");
    setAmendmentName(event.name || "");
    setAmendmentDescription(event.description || "");
    setAmendmentBannerLink(event.bannerLink || "");
    setAmendmentBannerFile(null);
    setAmendmentModalOpen(true);
  }, []);

  const closeAmendmentModal = useCallback(() => {
    setAmendmentModalOpen(false);
    setAmendmentEvent(null);
    setAmendmentMessage("");
    setAmendmentName("");
    setAmendmentDescription("");
    setAmendmentBannerLink("");
    setAmendmentBannerFile(null);
    setAmendmentSending(false);
  }, []);

  const handleSubmitAmendment = useCallback(async () => {
    if (!amendmentEvent?._id) return;
    try {
      setAmendmentSending(true);
      const formData = new FormData();
      if (amendmentName.trim()) formData.append("name", amendmentName.trim());
      if (amendmentDescription.trim() || isService) formData.append("description", amendmentDescription.trim());
      if (!isService && amendmentBannerLink.trim()) formData.append("bannerLink", amendmentBannerLink.trim());
      if (amendmentMessage.trim()) formData.append("message", amendmentMessage.trim());
      if (amendmentBannerFile) formData.append("banner", amendmentBannerFile);

      await axios.post(
        `${apiBasePath}/shop-event/${amendmentEvent._id}/request-amendment`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      toast.success("Amendment request sent. The admin team will review it.");
      closeAmendmentModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send amendment request. Please try again."
      );
    } finally {
      setAmendmentSending(false);
    }
  }, [
    amendmentEvent,
    amendmentName,
    amendmentDescription,
    amendmentBannerLink,
    amendmentMessage,
    amendmentBannerFile,
    closeAmendmentModal,
    isService,
    apiBasePath,
  ]);

  const hasAmendmentChanges = Boolean(
    amendmentEvent &&
      (
        (amendmentName.trim() && amendmentName.trim() !== (amendmentEvent.name || "").trim()) ||
        (amendmentDescription.trim() !== (amendmentEvent.description || "").trim()) ||
        (!isService && amendmentBannerLink.trim() !== (amendmentEvent.bannerLink || "").trim()) ||
        amendmentBannerFile ||
        amendmentMessage.trim()
      )
  );

  const handleDelete = useCallback(async (eventId) => {
    const ok = await openConfirmModal({
      title: `Delete ${entityLabel}?`,
      message: `Are you sure you want to delete this ${entityLabel.toLowerCase()}? This action cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;

    try {
      setDeletingId(eventId);
      await axios.delete(`${apiBasePath}/delete-shop-event/${eventId}`, {
        withCredentials: true,
      });
      toast.success(`${entityLabel} deleted`);
      // Reset fetch flag to allow refetch after delete
      hasFetchedRef.current = false;
      prevUserIdRef.current = null; // Force refetch
      // Defer refetch to prevent blocking
      const refetchEvents = () => {
        const currentUserId = user?._id;
        if (currentUserId) {
          dispatch(getAllEventsShop(currentUserId, apiNamespace));
        }
      };
      if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(refetchEvents, { timeout: 200 });
      } else {
        setTimeout(refetchEvents, 200);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Failed to delete the ${entityLabel.toLowerCase()}. Please try again.`
      );
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, user?._id, apiNamespace, apiBasePath, entityLabel, openConfirmModal]);

  const handleResubmit = useCallback(async (event) => {
    const ok = await openConfirmModal({
      title: `Resubmit ${entityLabel}?`,
      message: `Are you sure you want to resubmit this ${entityLabel.toLowerCase()} for review? Make sure you've made any necessary changes based on the rejection feedback.`,
      confirmLabel: "Resubmit",
    });
    if (!ok) return;

    try {
      setDeletingId(event._id);
      await axios.put(`${apiBasePath}/shop-event/${event._id}/resubmit`, {}, {
        withCredentials: true,
      });
      toast.success(`${entityLabel} resubmitted for review`);
      // Reset fetch flag to allow refetch after resubmit
      hasFetchedRef.current = false;
      prevUserIdRef.current = null; // Force refetch
      // Defer refetch to prevent blocking
      const refetchEvents = () => {
        const currentUserId = user?._id;
        if (currentUserId) {
          dispatch(getAllEventsShop(currentUserId, apiNamespace));
        }
      };
      if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(refetchEvents, { timeout: 200 });
      } else {
        setTimeout(refetchEvents, 200);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Failed to resubmit the ${entityLabel.toLowerCase()}. Please try again.`
      );
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, user?._id, apiNamespace, apiBasePath, entityLabel, openConfirmModal]);

  const handleWithdraw = useCallback(async (event) => {
    const ok = await openConfirmModal({
      title: `Withdraw ${entityLabel}?`,
      message: `Are you sure you want to withdraw this ${entityLabel.toLowerCase()}? You will receive a full refund of ${event.currency || "GBP"} ${Number(event.totalAmount || 0).toFixed(2)}. This action cannot be undone.`,
      confirmLabel: "Withdraw",
    });
    if (!ok) return;

    try {
      setDeletingId(event._id);
      await axios.put(`${apiBasePath}/shop-event/${event._id}/withdraw`, {}, {
        withCredentials: true,
      });
      toast.success(`${entityLabel} withdrawn and refund processed`);
      // Reset fetch flag to allow refetch after withdraw
      hasFetchedRef.current = false;
      prevUserIdRef.current = null; // Force refetch
      // Defer refetch to prevent blocking
      const refetchEvents = () => {
        const currentUserId = user?._id;
        if (currentUserId) {
          dispatch(getAllEventsShop(currentUserId, apiNamespace));
        }
      };
      if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(refetchEvents, { timeout: 200 });
      } else {
        setTimeout(refetchEvents, 200);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Failed to withdraw the ${entityLabel.toLowerCase()}. Please try again.`
      );
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, user?._id, apiNamespace, apiBasePath, entityLabel, openConfirmModal]);

  const handleCancelSubscription = useCallback(async (event) => {
    const ok = await openConfirmModal({
      title: isService ? "Cancel service renewals?" : "Cancel subscription renewals?",
      message: isService
        ? `Request to cancel renewals for this service? Your current paid ${
            event.billingCycle === "monthly" ? "month" : "week"
          } remains active until ${formatDate(event.approvedEnd)}.`
        : `Cancel auto-renew for this ${entityLabel.toLowerCase()} subscription? The current paid ${
            event.billingCycle === "monthly" ? "month" : "week"
          } will continue until ${formatDate(event.approvedEnd)}.`,
      confirmLabel: isService ? "Request cancellation" : "Cancel renewals",
    });
    if (!ok) return;

    try {
      setDeletingId(event._id);
      await axios.put(`${apiBasePath}/shop-event/${event._id}/cancel-subscription`, {}, {
        withCredentials: true,
      });
      toast.success(
        isService
          ? "Service cancellation request submitted. Current paid period remains active until end date."
          : "Subscription cancellation saved. Current paid period remains active until end date."
      );
      hasFetchedRef.current = false;
      prevUserIdRef.current = null;
      const refetchEvents = () => {
        const currentUserId = user?._id;
        if (currentUserId) {
          dispatch(getAllEventsShop(currentUserId, apiNamespace));
        }
      };
      if (typeof window !== "undefined" && window.requestIdleCallback) {
        window.requestIdleCallback(refetchEvents, { timeout: 200 });
      } else {
        setTimeout(refetchEvents, 200);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isService ? "request cancellation for" : "cancel"} ${entityLabel.toLowerCase()} subscription. Please try again.`
      );
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, user?._id, apiNamespace, apiBasePath, entityLabel, isService, openConfirmModal]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: `${entityLabel} Name`,
        minWidth: 220,
        flex: 1.3,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => renderStatusBadge(params.value),
      },
      {
        field: "billingCycle",
        headerName: "Subscription",
        minWidth: 110,
        flex: 0.6,
        valueFormatter: ({ value, row }) =>
          `${String(value || "weekly").toLowerCase() === "monthly" ? "Monthly" : "Weekly"}${
            row.isSubscriptionActive ? "" : " (stopped)"
          }`,
      },
      {
        field: "startDate",
        headerName: "Start date",
        minWidth: 140,
        flex: 0.7,
        valueFormatter: ({ value, row }) =>
          value ? formatDate(value) : (row.approvedStart ? formatDate(row.approvedStart) : formatDate(row.preferredStart)),
      },
      {
        field: "approvedEnd",
        headerName: "End date",
        minWidth: 140,
        flex: 0.7,
        valueFormatter: ({ value, row }) => formatDate(value || getProjectedEndDate(row)),
      },
      {
        field: "nextBillingDate",
        headerName: "Renewal date",
        minWidth: 140,
        flex: 0.7,
        valueFormatter: ({ value, row }) =>
          row?.autoRenew === false ? "Auto-renew off" : formatDate(value),
      },
      {
        field: "totalAmount",
        headerName: "Amount",
        minWidth: 120,
        flex: 0.7,
        valueFormatter: ({ value, row }) =>
          `${row.currency || "GBP"} ${Number(value || 0).toFixed(2)}`,
      },
      {
        field: "paymentMethod",
        headerName: "Payment",
        minWidth: 140,
        flex: 0.8,
        valueFormatter: ({ value }) =>
          value === "wallet"
            ? "Wallet"
            : value === "stripe" || value === "klarna"
            ? "Stripe"
            : value || "—",
      },
      {
        field: "bannerImage",
        headerName: "Banner",
        minWidth: 140,
        flex: 0.6,
        sortable: false,
        renderCell: (params) =>
          params.value ? (
            <img
              src={`${backend_url}${params.value}`}
              alt="Banner"
              className="h-12 w-12 object-contain rounded"
            />
          ) : (
            <span className="text-xs text-gray-400">No banner</span>
          ),
      },
      ...(!isService
        ? [{
            field: "bannerLink",
            headerName: "Banner Link",
            minWidth: 220,
            flex: 1.1,
            renderCell: (params) =>
              params.value ? (
                <a
                  href={params.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Open link
                </a>
              ) : (
                <span className="text-xs text-gray-400">—</span>
              ),
          }]
        : []),
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 170,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => {
          const isRejected = (params.row.status || "").toLowerCase() === "rejected";
          const isApproved = isEventApproved(params.row.status);
          return (
            <div className="flex items-center gap-1 flex-wrap">
              {!isRejected && !isApproved && (
                <>
                  <Button size="small" onClick={(e) => { e.stopPropagation(); openEditModal(params.row.original); }} title={`Edit ${entityLabel.toLowerCase()}`}>
                    <AiOutlineEdit size={18} />
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(params.id); }}
                    disabled={deletingId === params.id}
                  >
                    <AiOutlineDelete size={18} />
                  </Button>
                  {!isService && params.row.bannerLink && (
                    <Button size="small" component="a" href={params.row.bannerLink} target="_blank" onClick={(e) => e.stopPropagation()}>
                      <AiOutlineEye size={18} />
                    </Button>
                  )}
                </>
              )}
              {!isRejected && isApproved && (
                <>
                  {(params.row.autoRenew || getRenewingSubscriptionHint(params.row.original)) && (
                    <Button
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleCancelSubscription(params.row.original); }}
                      disabled={deletingId === params.id}
                      style={{ color: "#ef4444", fontSize: "0.7rem" }}
                      title={
                        isService
                          ? "Request to cancel service (current paid period remains active)"
                          : "Cancel future renewals (current paid period remains active)"
                      }
                    >
                      {isService ? "Request to cancel service" : "Cancel subscription"}
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); openAmendmentModal(params.row.original); }}
                    style={{ color: "#38513b", fontSize: "0.7rem" }}
                    title={`Request amendment (approved ${entityLabelPlural.toLowerCase()} cannot be edited directly)`}
                  >
                    Request amendment
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(params.id); }}
                    disabled={deletingId === params.id}
                  >
                    <AiOutlineDelete size={18} />
                  </Button>
                  {!isService && params.row.bannerLink && (
                    <Button size="small" component="a" href={params.row.bannerLink} target="_blank" onClick={(e) => e.stopPropagation()}>
                      <AiOutlineEye size={18} />
                    </Button>
                  )}
                </>
              )}
              {isRejected && (
                <>
                  <Button
                    size="small"
                    onClick={() => handleResubmit(params.row.original)}
                    disabled={deletingId === params.id}
                    style={{ color: "#10b981" }}
                    title={`Resubmit ${entityLabel.toLowerCase()}`}
                  >
                    Resubmit
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleWithdraw(params.row.original)}
                    disabled={deletingId === params.id}
                    style={{ color: "#ef4444" }}
                    title={`Withdraw ${entityLabel.toLowerCase()} and get refund`}
                  >
                    Withdraw
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      deletingId,
      openEditModal,
      openAmendmentModal,
      handleDelete,
      handleResubmit,
      handleWithdraw,
      handleCancelSubscription,
      getRenewingSubscriptionHint,
      isEventApproved,
      entityLabel,
      entityLabelPlural,
      isService,
    ]
  );

  // Memoize handleFormChange to prevent unnecessary re-renders
  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Memoize closeModal to prevent unnecessary re-renders
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCurrentEvent(null);
    setFormState(initialFormState);
    setBannerFile(null);
    setIsSaving(false);
  }, []);

  const handleSave = useCallback(async (event) => {
    event.preventDefault();
    if (!currentEvent) return;

    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append("name", formState.name || "");
      if (!isService) {
        formData.append("bannerLink", formState.bannerLink || "");
      } else {
        formData.append("description", formState.description || "");
      }
      if (bannerFile) {
        formData.append("banner", bannerFile);
      }

      await axios.put(
        `${apiBasePath}/update-shop-event/${currentEvent._id}`,
        formData,
        { withCredentials: true }
      );

      toast.success(`${entityLabel} updated`);
      closeModal();
      // Reset fetch flag to allow refetch after update
      hasFetchedRef.current = false;
      prevUserIdRef.current = null; // Force refetch
      // Defer refetch to prevent blocking
      const refetchEvents = () => {
        if (user?._id) {
          dispatch(getAllEventsShop(user._id, apiNamespace));
        }
      };
      if (typeof window !== 'undefined' && window.requestIdleCallback) {
        window.requestIdleCallback(refetchEvents, { timeout: 200 });
      } else {
        setTimeout(refetchEvents, 200);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || `Failed to update ${entityLabel.toLowerCase()}`;
      toast.error(message);
      setIsSaving(false);
    }
  }, [currentEvent, formState, bannerFile, user?._id, dispatch, apiNamespace, apiBasePath, entityLabel, isService]);

  const renderBannerPreview = () => {
    if (bannerFile) {
      return (
        <img
          src={URL.createObjectURL(bannerFile)}
          alt="New banner"
          className="h-24 w-full max-w-[200px] object-contain rounded border"
        />
      );
    }

    if (currentEvent?.bannerImage) {
      return (
        <img
          src={`${backend_url}${currentEvent.bannerImage}`}
          alt="Current banner"
          className="h-24 w-full max-w-[200px] object-contain rounded border"
        />
      );
    }

    return <span className="text-xs text-gray-400">No banner uploaded</span>;
  };

  const handleRowClick = useCallback((params) => {
    openEditModal(params.row.original);
  }, [openEditModal]);

  // Don't block rendering - show content even if loading
  // DataGrid will show its own loading indicator
  return (
    <>
      <div
        id={isService ? "services-transactions" : "events-transactions"}
        className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <div className="p-4 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">My {entityLabelPlural}</h2>
              <p className="text-sm text-slate-500 mt-1">
                Manage your {entityLabel.toLowerCase()} advertisements and track their status.
              </p>
            </div>
            <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-700">Filter by:</label>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
            >
              <option value="all">All {entityLabelPlural}</option>
              <option value="days">Next 7 Days</option>
              <option value="week">Next Week</option>
              <option value="month">Next Month</option>
            </select>
            </div>
          </div>
        </div>
        <div className="p-4">
          <DashboardTableScroll>
            <div className="w-max min-w-[1100px]">
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 20, 50]}
                disableSelectionOnClick
                autoHeight
                loading={isLoading && rows.length === 0}
                initialState={{ sorting: { sortModel: [{ field: "startDate", sort: "asc" }] } }}
                onRowClick={handleRowClick}
                sx={{
                  width: "max-content",
                  minWidth: "100%",
                  border: "none",
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #e2e8f0",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#1e293b",
                    borderBottom: "2px solid #334155",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    color: "#ffffff",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      color: "#ffffff",
                    },
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f8fafc",
                  },
                  "& .MuiDataGrid-cell[data-field='actions'], & .MuiDataGrid-cell[data-field='Preview']": {
                    cursor: "pointer",
                  },
                }}
              />
            </div>
          </DashboardTableScroll>
        </div>
      </div>

      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="w-full max-w-3xl bg-white rounded-md shadow-lg max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Edit {entityLabel}</h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeModal();
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {entityLabel} Name
                  </label>
                  <input
                    type="text"
                    value={formState.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
                {!isService ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banner Link
                    </label>
                    <input
                      type="url"
                      value={formState.bannerLink}
                      onChange={(e) => handleFormChange("bannerLink", e.target.value)}
                      className="w-full border rounded-md px-3 py-2"
                      placeholder="https://example.com"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Description
                    </label>
                    <textarea
                      value={formState.description || ""}
                      onChange={(e) => handleFormChange("description", e.target.value)}
                      className="w-full border rounded-md px-3 py-2 min-h-[96px]"
                      placeholder="Describe the service..."
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Banner Image
                </label>
                <div className="flex items-center gap-4 flex-wrap">
                  {renderBannerPreview()}
                  <label className="cursor-pointer text-sm font-medium text-[#38513b]">
                    <span className="underline">Choose Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleEditBannerFileSelect}
                    />
                  </label>
                  <p className="text-xs text-slate-500">
                    Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB per image. Required:{" "}
                    {EVENT_BANNER_SIZE_LABEL}.
                  </p>
                  {bannerFile && (
                    <button
                      type="button"
                      className="text-xs text-red-500"
                      onClick={() => setBannerFile(null)}
                    >
                      Remove selection
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Required banner size: {EVENT_BANNER_SIZE_LABEL}. Other sizes open a crop tool.
                  Uploading a new image will replace the current banner.
                </p>
              </div>

              {currentEvent && currentEvent.status === "rejected" && currentEvent.rejectionReason && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-rose-800 mb-1">Rejection Reason:</h3>
                    <p className="text-sm text-rose-700">{currentEvent.rejectionReason}</p>
                  </div>
                  <div className="pt-2 border-t border-rose-200 space-y-2">
                    <p className="text-sm font-medium text-rose-800">Actions:</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          closeModal();
                          handleResubmit(currentEvent);
                        }}
                        className="flex-1 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition"
                      >
                        ✓ Resubmit for Review
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          closeModal();
                          handleWithdraw(currentEvent);
                        }}
                        className="flex-1 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition"
                      >
                        ✗ Withdraw & Get Refund
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  className="w-[200px] bg-[#38513B] h-[50px] my-3 flex items-center justify-center rounded-xl cursor-pointer font-[600] text-white px-5"
                  onClick={closeModal}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-[200px] bg-black h-[50px] my-3 flex items-center justify-center rounded-xl cursor-pointer font-[600] text-white px-5"
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {amendmentModalOpen && amendmentEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAmendmentModal();
          }}
        >
          <div
            className="w-full max-w-lg bg-white rounded-md shadow-lg p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Request amendment</h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={closeAmendmentModal}
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Approved {entityLabelPlural.toLowerCase()} cannot be edited directly. Fill in the fields you want to change; the admin team will review and apply your request.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{entityLabel} name</label>
                <input
                  type="text"
                  value={amendmentName}
                  onChange={(e) => setAmendmentName(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                  placeholder="Leave unchanged or enter new name"
                />
              </div>
              {isService ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service description</label>
                  <textarea
                    value={amendmentDescription}
                    onChange={(e) => setAmendmentDescription(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[96px] focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                    placeholder="Describe the updated service details..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banner link</label>
                  <input
                    type="url"
                    value={amendmentBannerLink}
                    onChange={(e) => setAmendmentBannerLink(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New banner image (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full text-sm text-slate-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#38513b] file:text-white file:text-sm"
                  onChange={handleAmendmentBannerFileSelect}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Required: {EVENT_BANNER_SIZE_LABEL}. Other sizes open a crop tool.
                </p>
                {amendmentBannerFile && (
                  <p className="text-xs text-slate-500 mt-1">{amendmentBannerFile.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                <textarea
                  value={amendmentMessage}
                  onChange={(e) => setAmendmentMessage(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
                  placeholder="Any additional notes for the admin..."
                />
              </div>
            </div>

            {isService &&
              (getRenewingSubscriptionHint(amendmentEvent) || isCancellationRecorded(amendmentEvent)) && (
              <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs text-rose-700 mb-2">
                  Need to stop renewals for this service?
                </p>
                <button
                  type="button"
                  className="w-full rounded-md border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                  onClick={async () => {
                    await handleCancelSubscription(amendmentEvent);
                    closeAmendmentModal();
                  }}
                  disabled={
                    amendmentSending ||
                    deletingId === amendmentEvent?._id ||
                    isCancellationRecorded(amendmentEvent)
                  }
                >
                  {deletingId === amendmentEvent?._id
                    ? "Requesting..."
                    : isCancellationRecorded(amendmentEvent)
                    ? "Renewals already cancelled"
                    : "Request to cancel renewals"}
                </button>
              </div>
            )}
            {isService &&
              !getRenewingSubscriptionHint(amendmentEvent) &&
              !isCancellationRecorded(amendmentEvent) && (
                <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-600">
                    This service run does not auto-renew (single period or upfront). Nothing to cancel for future
                    billing—use amendment or contact support if you need an early stop.
                  </p>
                </div>
              )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={closeAmendmentModal}
                disabled={amendmentSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-[#38513b] rounded-xl text-sm font-medium text-white hover:bg-[#2f4232] disabled:opacity-60"
                onClick={handleSubmitAmendment}
                disabled={amendmentSending || !hasAmendmentChanges}
              >
                {amendmentSending ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModalOpen && (
        <div
          className="fixed inset-0 z-[100001] flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeConfirmModal();
          }}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-slate-900 mb-2">{confirmModalConfig.title}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line">{confirmModalConfig.message}</p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={closeConfirmModal}
              >
                Back
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-rose-600 text-sm font-semibold text-white hover:bg-rose-700"
                onClick={handleConfirmModalConfirm}
              >
                {confirmModalConfig.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <EventBannerCropModal
        open={Boolean(editCropSession)}
        imageSrc={editCropSession?.src}
        originalFileName={editCropSession?.fileName}
        onConfirm={handleEditCropConfirm}
        onCancel={handleEditCropCancel}
      />
      <EventBannerCropModal
        open={Boolean(amendmentCropSession)}
        imageSrc={amendmentCropSession?.src}
        originalFileName={amendmentCropSession?.fileName}
        onConfirm={handleAmendmentCropConfirm}
        onCancel={handleAmendmentCropCancel}
      />
    </>
  );
};

export default AllEvents;
